import React, { useState } from 'react';
import { Button, Card, CardContent, Typography, Grid } from '@mui/material';
import { Volume2, RotateCcw } from 'lucide-react';

interface AACSymbol {
  id: string;
  label: string;
  emoji: string;
  shortcut: string;
}

const DEFAULT_SYMBOLS: AACSymbol[] = [
  { id: '1', label: 'I / ME', emoji: '🖐️', shortcut: '1' },
  { id: '2', label: 'WANT', emoji: '❤️', shortcut: '2' },
  { id: '3', label: 'WATER', emoji: '💧', shortcut: '3' },
  { id: '4', label: 'FOOD', emoji: '🍎', shortcut: '4' },
  { id: '5', label: 'TOILET', emoji: '🚽', shortcut: '5' },
  { id: '6', label: 'HAPPY', emoji: '😊', shortcut: '6' },
  { id: '7', label: 'PAIN / HURT', emoji: '😢', shortcut: '7' },
  { id: '8', label: 'HOME', emoji: '🏠', shortcut: '8' },
];

export const AACBoard: React.FC = () => {
  const [selectedPhrase, setSelectedPhrase] = useState<string[]>([]);

  const handleSelectSymbol = (symbol: AACSymbol) => {
    setSelectedPhrase((prev) => [...prev, symbol.label]);
  };

  const handleSpeakSentence = () => {
    if (selectedPhrase.length === 0) return;
    const text = selectedPhrase.join(' ');
    const utterance = new SpeechSynthesisUtterance(text);
    window.speechSynthesis.speak(utterance);
  };

  const handleClear = () => {
    setSelectedPhrase([]);
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-md border border-slate-200">
      <div className="flex items-center justify-between mb-4">
        <div>
          <Typography variant="h5" className="font-bold text-slate-800">
            AAC Augmentative Communication Board
          </Typography>
          <Typography variant="body2" className="text-slate-500">
            Touch, Switch Scan, or Keyboard (Keys 1-8)
          </Typography>
        </div>

        <div className="flex gap-2">
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<Volume2 className="w-4 h-4" />}
            onClick={handleSpeakSentence}
            disabled={selectedPhrase.length === 0}
          >
            Speak All
          </Button>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={<RotateCcw className="w-4 h-4" />}
            onClick={handleClear}
            disabled={selectedPhrase.length === 0}
          >
            Clear
          </Button>
        </div>
      </div>

      {/* Spoken Phrase Preview Bar */}
      <div 
        className="p-4 bg-slate-100 rounded-lg border border-slate-300 min-h-[60px] mb-6 flex items-center gap-2 overflow-x-auto"
        role="status"
        aria-live="polite"
      >
        <Typography variant="subtitle2" className="text-slate-400 font-medium mr-2">
          Constructed Phrase:
        </Typography>
        {selectedPhrase.length === 0 ? (
          <span className="text-slate-400 italic">Tap symbols below to build a sentence...</span>
        ) : (
          selectedPhrase.map((word, idx) => (
            <span key={idx} className="px-3 py-1 bg-blue-600 text-white rounded-full font-bold text-sm">
              {word}
            </span>
          ))
        )}
      </div>

      {/* AAC Grid */}
      <Grid container spacing={2}>
        {DEFAULT_SYMBOLS.map((symbol) => (
          <Grid item xs={6} sm={3} key={symbol.id}>
            <Card 
              className="cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all border-2 border-slate-200 text-center"
              onClick={() => handleSelectSymbol(symbol)}
              tabIndex={0}
              role="button"
              aria-label={`AAC Symbol ${symbol.label}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSelectSymbol(symbol);
                }
              }}
            >
              <CardContent className="p-4">
                <div className="text-4xl mb-2">{symbol.emoji}</div>
                <Typography variant="subtitle1" className="font-bold text-slate-800">
                  {symbol.label}
                </Typography>
                <span className="text-xs text-slate-400 font-mono mt-1 block">Key: {symbol.shortcut}</span>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};
